// NotificationBell
//
// Top-bar bell icon with a badge count. Click → dropdown showing
// actionable items aggregated from multiple sources:
//
//   * Client document uploads awaiting firm review
//     (portal_document_requests where status = 'uploaded')
//   * Active firm-wide announcements (so they don't get lost after the
//     banner is dismissed)
//   * Deadlines due in the next 7 days that the firm should be aware of
//
// Each row links to the relevant place. The dropdown is dismissed by
// clicking outside or pressing Esc.
//
// Polling: refreshes whenever the bell is opened, plus a lightweight
// background poll every 5 minutes when the tab is visible. Not real-time —
// real-time would require a Supabase realtime subscription per source,
// which is overkill for an in-app inbox of this scale.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Bell, FileText, Megaphone, CalendarClock, ChevronRight,
} from 'lucide-react';
import {
  listPortalDocumentRequestsForFirm, listMyAnnouncements, listDeadlines,
} from '../lib/db';
import { useFirm } from '../contexts/FirmContext';
import { useRoute } from '../lib/router';
import { formatRelative } from '../lib/formatters';

export default function NotificationBell() {
  const { firmId } = useFirm();
  const [, navigate] = useRoute();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState({ uploads: [], announcements: [], deadlines: [] });
  const [loading, setLoading] = useState(false);
  const popoverRef = useRef(null);

  const totalCount =
    items.uploads.length +
    items.announcements.length +
    items.deadlines.length;

  const refresh = useCallback(async () => {
    if (!firmId) return;
    setLoading(true);
    try {
      const [reqRes, annRes, dlRes] = await Promise.all([
        listPortalDocumentRequestsForFirm(),
        listMyAnnouncements(),
        listDeadlines(firmId, true),
      ]);
      const uploads = (Array.isArray(reqRes.data) ? reqRes.data : [])
        .filter((r) => r.status === 'uploaded')
        .slice(0, 8);
      const announcements = (Array.isArray(annRes.data) ? annRes.data : [])
        .filter((a) => a.active !== false)
        .slice(0, 5);
      const now = Date.now();
      const nextWeek = now + 7 * 86400000;
      const deadlines = (dlRes.data || [])
        .filter((d) => {
          if (d.status !== 'open' && d.status !== 'pending') return false;
          const t = d.due_date ? new Date(d.due_date).getTime() : 0;
          return t >= now - 86400000 && t <= nextWeek;
        })
        .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
        .slice(0, 6);
      setItems({ uploads, announcements, deadlines });
    } catch (_) {
      // Best-effort — silently keep prior items on error so a network blip
      // doesn't clear what the user can see.
    } finally {
      setLoading(false);
    }
  }, [firmId]);

  // Refresh on mount + every 5 minutes while the tab is visible.
  useEffect(() => {
    refresh();
    const tick = setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, 5 * 60 * 1000);
    const onVis = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(tick); document.removeEventListener('visibilitychange', onVis); };
  }, [refresh]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const handleOpen = () => {
    setOpen((o) => !o);
    if (!open) refresh();
  };

  const go = (path) => { navigate(path); setOpen(false); };

  return (
    <div className="relative" ref={popoverRef}>
      <button
        onClick={handleOpen}
        className="relative p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
        aria-label="Notifications"
        aria-expanded={open}
      >
        <Bell size={18} className="text-slate-600 dark:text-slate-300" />
        {totalCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white dark:border-slate-900">
            {totalCount > 99 ? '99+' : totalCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 max-h-[70vh] overflow-y-auto bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800 z-50">
          <div className="sticky top-0 px-4 py-3 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">Notifications</p>
            {loading && <span className="text-xs text-slate-400">Refreshing…</span>}
          </div>

          {totalCount === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-slate-400">
              <Bell size={24} className="mx-auto mb-2 opacity-40" />
              You're all caught up.
            </div>
          ) : (
            <div>
              {items.uploads.length > 0 && (
                <Section title="Client uploads awaiting review" icon={FileText} tone="amber">
                  {items.uploads.map((u) => (
                    <Row
                      key={u.id}
                      onClick={() => go(u.matter_id ? `/matters/${u.matter_id}` : '/documents')}
                      title={u.title}
                      subtitle={
                        [
                          [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || 'Client',
                          u.matter_title,
                          u.uploaded_at ? formatRelative(u.uploaded_at) : null,
                        ].filter(Boolean).join(' · ')
                      }
                    />
                  ))}
                </Section>
              )}

              {items.announcements.length > 0 && (
                <Section title="Announcements" icon={Megaphone} tone="indigo">
                  {items.announcements.map((a) => (
                    <Row
                      key={a.id}
                      onClick={() => setOpen(false)}
                      title={a.title || 'Announcement'}
                      subtitle={a.body ? a.body.slice(0, 140) : ''}
                    />
                  ))}
                </Section>
              )}

              {items.deadlines.length > 0 && (
                <Section title="Deadlines this week" icon={CalendarClock} tone="red">
                  {items.deadlines.map((d) => (
                    <Row
                      key={d.id}
                      onClick={() => go(d.matter_id ? `/matters/${d.matter_id}` : '/deadlines')}
                      title={d.title || d.description || 'Deadline'}
                      subtitle={
                        [
                          d.due_date ? `Due ${formatRelative(d.due_date)}` : null,
                          d.matter?.title,
                        ].filter(Boolean).join(' · ')
                      }
                    />
                  ))}
                </Section>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const TONES = {
  amber:  'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  indigo: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  red:    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

function Section({ title, icon: Icon, tone, children }) {
  return (
    <div className="py-2">
      <div className="px-4 py-2 flex items-center gap-2">
        <span className={`inline-flex items-center justify-center w-5 h-5 rounded-md ${TONES[tone] || TONES.indigo}`}>
          <Icon size={11} />
        </span>
        <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">
          {title}
        </p>
      </div>
      <ul className="divide-y divide-slate-100 dark:divide-slate-800">{children}</ul>
    </div>
  );
}

function Row({ title, subtitle, onClick }) {
  return (
    <li>
      <button
        onClick={onClick}
        className="w-full px-4 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800 transition flex items-center gap-2"
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{title}</p>
          {subtitle && (
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{subtitle}</p>
          )}
        </div>
        <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />
      </button>
    </li>
  );
}
